/**
 * Pure reads over a settled (or unsettled) `/billing/status` lookup.
 *
 * A leaf module on purpose: `subscription-service.ts` reaches the app runtime
 * through its query imports, which panics without SSR env at import time. Kept
 * here, these stay importable from a plain unit test.
 *
 * **Read `entitlement.source`, never `entitlement.state`.** The state word
 * describes the subscription; `source` answers "does this person have access?".
 * They come apart in exactly the case that matters: a subscription cancelled
 * outright inside the period it was paid for reports `state: 'canceled'` and
 * still entitles (the time is already bought), and `past_due` entitles through
 * Stripe's retry window. Mapping the state word to a boolean would revoke paid
 * access from both cohorts. The contract puts it plainly — `source` is `null`
 * "only what it should: nothing grants access" — so it is the one field these
 * readers may branch on for access.
 */

/**
 * Whether the paywall counts as shown for `premium_paywall_interaction:
 * opened`.
 *
 * Not a `boolean`, so the neighbouring subscription reads cannot be swapped in
 * by mistake — they are all boolean and all plausible-sounding, but
 * `notEntitledFor` is false while the lookup is failing and `entitledFor` is
 * inverted. Either one would compile, report a silently wrong number, and show
 * up in no test.
 */
export type PaywallShown = 'shown' | 'not-shown';

/** The shape these readers need — a TanStack query over `/billing/status`. */
type BillingStatusQueryLike = {
  readonly data?:
    | {
        readonly entitlement: { readonly source: string | null };
      }
    | undefined;
  readonly isError: boolean;
};

/**
 * False until `/billing/status` has settled, and false while it is failing.
 *
 * Safe for restricting free users, because it never flags a paying user as
 * free-tier prematurely — a blip cannot revoke paid features.
 */
export const notEntitledFor = (query: BillingStatusQueryLike): boolean =>
  query.data?.entitlement.source === null;

/**
 * Also false until settled, so an entitled user reads as free during load.
 *
 * Reactive renders self-correct once data lands; point-in-time decisions (event
 * handlers) must settle the query first rather than gate on this.
 */
export const entitledFor = (query: BillingStatusQueryLike): boolean => {
  const source = query.data?.entitlement.source;
  return source !== undefined && source !== null;
};

/**
 * Shown when the lookup says no access, or when it failed. Not shown while it
 * has not answered.
 *
 * The lookup has three outcomes, not two, and collapsing any pair produces a
 * specific silent wrong number. Pending-as-not-entitled counts every subscriber,
 * because they render `/app/premium` for a beat before the redirect lands.
 * Failed-as-not-shown undercounts during an outage with no signal at all.
 *
 * It reads `isError` even when `data` is present: TanStack keeps the last good
 * value alongside a failed refetch, and "we knew a minute ago" is not the same
 * claim as "we know".
 */
export const paywallShownFor = (query: BillingStatusQueryLike): PaywallShown =>
  query.data?.entitlement.source === null || query.isError
    ? 'shown'
    : 'not-shown';

type PaywallBounceQueryLike = {
  readonly data?:
    | {
        readonly entitlement: {
          readonly source: string | null;
          readonly state: string;
        };
        readonly billing: { readonly provider: string };
      }
    | undefined;
};

export const lapsingStripeFor = (status: {
  readonly entitlement: { readonly state: string };
  readonly billing: { readonly provider: string };
}) =>
  status.billing.provider !== 'stripe' ||
  status.entitlement.state === 'canceled';

export const bouncedFromPaywallFor = (query: PaywallBounceQueryLike) => {
  const status = query.data;
  if (status === undefined || status.entitlement.source === null) return false;
  return !(status.entitlement.source === 'stripe' && lapsingStripeFor(status));
};

/**
 * Access that is still running but will not renew: a pending cancel, or a
 * subscription cancelled outright inside a period that was already paid for.
 * The second reports `cancel_at_period_end: false`, so the flag alone misses
 * the whole immediate-cancel cohort.
 */
export const expiringPlanFor = (status: {
  readonly entitlement: {
    readonly source: string | null;
    readonly state: string;
  };
  readonly billing: { readonly cancel_at_period_end: boolean };
}) =>
  status.entitlement.source === 'stripe' &&
  (status.billing.cancel_at_period_end ||
    status.entitlement.state === 'canceled');
